import React from 'react';

import { Footer, Meta } from '$partials/global/index.js';
import ModuleDispatcher from '$partials/modules/dispatcher.jsx';
import { Button } from '$partials/ui';
import { useLocale } from '$contexts/locale.jsx';
import Image from '$partials/media/image';
import { resolvePath } from '$utilities/routes';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').ContactListingPage}
 */

const ContactListingPage = ({ data, domain }) => {

    const page = data?.page;
    const {
        formatURI,
        translate: t,
    } = useLocale();

    if (!page) { return null; }


    return (
        <>
            <Meta page={page} />

            <main id='main-content'>
                <div className='min-h-screen'>
                    <section className='layout-container layout-site-outer-padding flex items-center flex-col text-center pb-24 pt-80 md:pb-48 md:pt-[152px]'>
                        <h1 className='type-style-3-extended text-text-primary'>
                            {page?.pageTitle}
                        </h1>


                        {/* body copy */}
                        {page?.bodyCopy && (
                            <p className='text-text-secondary type-style-body mt-10 md:mt-16 sm:w-10/12 md:w-6/12'>
                                {page.bodyCopy}
                            </p>
                        )}

                        {/* cta buttons */}
                        {(page?.cta1Link || page?.cta2Link) && (
                            <div className='flex flex-wrap gap-8 mt-24 md:mt-32'>
                                {page?.cta1Link && (
                                    <Button
                                        as='a'
                                        className='button button--primary'
                                        href={formatURI(page.cta1Link)}>
                                        <span className='button__label--animate'>
                                            <span>{page.cta1Label}</span>
                                        </span>
                                    </Button>
                                )}
                                {page?.cta2Link && (
                                    <Button
                                        as='a'
                                        className='button button--secondary'
                                        href={formatURI(page.cta2Link)}>
                                        <span className='button__label--animate'>
                                            <span>{page.cta2Label}</span>
                                        </span>
                                    </Button>
                                )}
                            </div>
                        )}
                    </section>

                    {/* Contact page cards */}
                    <section className='relative overflow-hidden py-40 md:py-80'>
                        <div className='layout-container layout-site-outer-padding'>
                            <div className='bg-white'>
                                {/* Desktop */}
                                {page?.contactPagesCollection?.items?.length > 0 && (
                                    <div className='grid grid-cols-1 md:grid-cols-3 gap-16 mt-24'>
                                        {page.contactPagesCollection.items.map((item, i) =>
                                            <div
                                                className='flex flex-col justify-between text-center items-center bg-surface-raised p-24 md:p-32 rounded'
                                                key={i}>

                                                {item?.listingEyebrowLogo && (
                                                    <Image
                                                        className='object-cover h-full w-full'
                                                        containerClassName='w-48 overflow-hidden mb-32'
                                                        src={item?.listingEyebrowLogo?.src}/>
                                                )}

                                                {item?.listingHeading && (
                                                    <h3 className='type-style-4 text-text-primary mb-10'>
                                                        {item.listingHeading}
                                                    </h3>
                                                )}

                                                {item?.listingBodyCopy && (
                                                    <p className='type-style-body text-text-secondary'>
                                                        {item.listingBodyCopy}
                                                    </p>
                                                )}

                                                {item?.slug && item?.listingCtaLabel && (
                                                    <Button
                                                        as='a'
                                                        className='mt-40 block button button--outline'
                                                        href={resolvePath({ slug: item.slug, cfMetadata: item.cfMetadata })}>
                                                        <span className='button__label--animate'>
                                                            <span>{item.listingCtaLabel}</span>
                                                        </span>
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>

                    {/* Content */}
                    {(page?.modules?.items?.length > 0) && (
                        page.modules.items.map((module, i) => {
                            if (!module) { return null; }
                            return (
                                <ModuleDispatcher
                                    key={i}
                                    module={module}
                                    previousTypename={page.modules.items[i - 1]?.__typename ?? null}
                                    nextTypename={page.modules.items[i + 1]?.__typename ?? null}/>
                            );
                        })
                    )}

                    <div className='pt-8'>
                        <Footer
                            domain={domain}
                            menu={data?.footer} />
                    </div>
                </div>
            </main>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default ContactListingPage;
